import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Alert, Badge } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  createRequest,
  selectAllRequestsLoading
} from 'store/allRequestsSlice';
import { fetchAssets, selectAssets, AssetDataType } from 'store/assetsSlice';
import { getCategories } from 'api/assetsAPI';
import { CreateRequestData, Category } from 'api/assetsAPI';

interface CreateRequestModalProps {
  show: boolean;
  handleClose: () => void;
}

const CreateRequestModal = ({ show, handleClose }: CreateRequestModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectAllRequestsLoading);
  const assets = useSelector(selectAssets);

  const [formData, setFormData] = useState<CreateRequestData>({
    asset_id: 0,
    category_id: 0,
    request_note: '',
    priority: 'medium'
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch assets and categories when modal opens
  useEffect(() => {
    if (show) {
      loadAssetsAndCategories();
    }
  }, [show]);

  const loadAssetsAndCategories = async () => {
    setAssetsLoading(true);
    setCategoriesLoading(true);
    setError(null);

    try {
      // Fetch assets from Redux store
      await dispatch(fetchAssets()).unwrap();

      // Fetch categories directly from API - NO COMPANY FILTER

      const categoriesData = await getCategories();

      setCategories(categoriesData);
    } catch (err) {
      console.error('❌ Failed to load data:', err);
      setError('Failed to load assets and categories. Please try again.');
    } finally {
      setAssetsLoading(false);
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    if (name === 'asset_id' || name === 'category_id') {
      setFormData(prev => ({
        ...prev,
        [name]: Number(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation - Only category and request note are required now
    if (!formData.category_id || !formData.request_note.trim()) {
      setError('Please select a category and provide request details');
      return;
    }

    try {
      await dispatch(createRequest(formData)).unwrap();
      handleClose();

      // Reset form
      setFormData({
        asset_id: 0,
        category_id: 0,
        request_note: '',
        priority: 'medium'
      });
    } catch (err) {
      console.error('Failed to create request:', err);
      setError('Failed to create request. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setError(null);
    handleClose();
  };

  // Filter available assets (status === 'available')
  const availableAssets = assets.filter(
    (asset: AssetDataType) => asset.status === 'available'
  );

  // Get selected asset details
  const selectedAsset =
    formData.asset_id > 0
      ? assets.find((asset: AssetDataType) => asset.id === formData.asset_id)
      : null;

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Create Asset Request</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row className="g-3">
            {/* Asset Selection - Now Optional */}
            <Col md={6}>
              <Form.Group controlId="asset_id">
                <Form.Label>Select Asset (Optional)</Form.Label>
                <Form.Select
                  name="asset_id"
                  value={formData.asset_id}
                  onChange={handleInputChange}
                  disabled={assetsLoading}
                >
                  <option value={0}>Choose an asset (optional)...</option>
                  {assetsLoading ? (
                    <option>Loading assets...</option>
                  ) : (
                    availableAssets.map((asset: AssetDataType) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name}
                      </option>
                    ))
                  )}
                </Form.Select>
                <Form.Text className="text-muted">
                  {assetsLoading
                    ? 'Loading available assets...'
                    : availableAssets.length === 0
                    ? 'No assets available'
                    : `${availableAssets.length} available assets`}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Category Selection - Now always accessible and shows ALL categories */}
            <Col md={6}>
              <Form.Group controlId="category_id">
                <Form.Label>Category *</Form.Label>
                <Form.Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  required
                  disabled={categoriesLoading}
                >
                  <option value={0}>
                    {categoriesLoading
                      ? 'Loading categories...'
                      : 'Choose a category...'}
                  </option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select the type of asset you need
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Priority */}
            <Col md={6}>
              <Form.Group controlId="priority">
                <Form.Label>Priority *</Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  How urgent is this request?
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Request Notes */}
            <Col md={12}>
              <Form.Group controlId="request_note">
                <Form.Label>Request Details *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="request_note"
                  value={formData.request_note}
                  onChange={handleInputChange}
                  placeholder={
                    formData.asset_id > 0
                      ? 'Please describe why you need this specific asset, any specific requirements, and how it will help your work...'
                      : 'Please describe the asset you need including details like device name, specifications, brand preferences, and how it will help your work...'
                  }
                  required
                />
                <Form.Text className="text-muted">
                  {formData.asset_id > 0
                    ? 'Provide detailed information about why you need this specific asset'
                    : 'Provide detailed information about the type of asset you need including specifications and requirements'}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Asset Details Preview - Only show if asset is selected */}
            {selectedAsset && (
              <Col md={12}>
                <div className="border rounded p-3 bg-light">
                  <h6 className="mb-2">Selected Asset Details</h6>
                  <Row className="g-2 small">
                    <Col md={4}>
                      <strong>Brand:</strong> {selectedAsset.brand}
                    </Col>
                    <Col md={4}>
                      <strong>Model:</strong> {selectedAsset.model}
                    </Col>
                    <Col md={4}>
                      <strong>Condition:</strong> {selectedAsset.condition}
                    </Col>
                    <Col md={4}>
                      <strong>Category:</strong>{' '}
                      {selectedAsset.category_name || 'N/A'}
                    </Col>
                    <Col md={4}>
                      <strong>Location:</strong> {selectedAsset.location}
                    </Col>
                    <Col md={4}>
                      <strong>Status:</strong>
                      <Badge
                        bg={
                          selectedAsset.status === 'available'
                            ? 'success'
                            : selectedAsset.status === 'assigned'
                            ? 'primary'
                            : selectedAsset.status === 'maintenance'
                            ? 'warning'
                            : 'secondary'
                        }
                        className="ms-1"
                      >
                        {selectedAsset.status}
                      </Badge>
                    </Col>
                    <Col md={12}>
                      <strong>Description:</strong> {selectedAsset.description}
                    </Col>
                  </Row>
                </div>
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || assetsLoading || categoriesLoading}
          >
            {loading ? 'Creating Request...' : 'Create Request'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateRequestModal;
